import axios from "@/plugins/axios";
import { actionTypes, mutationTypes } from "@/store/Yield/types";
import { prepareBodyForInterval } from "@/helpers/data";
// import { showMessage } from "@/helpers/messages";

// "Last request wins" guard. A yield fetch dispatched by a previous YieldCards
// mount (e.g. before a publisher switch triggered a remount) can resolve after
// the newer fetch and overwrite the store with the old publisher's data and
// available_slot_names. Each committing request cancels the previous in-flight
// one and only the latest is allowed to touch the store.
const createLatestRequestGuard = () => {
    let latestId = 0;
    let cancelSource = null;
    return {
        start() {
            if (cancelSource) {
                cancelSource.cancel("Superseded by a newer request");
            }
            cancelSource = axios.CancelToken.source();
            latestId += 1;
            const id = latestId;
            return { cancelToken: cancelSource.token, isLatest: () => id === latestId };
        },
    };
};

const yieldDataGuard = createLatestRequestGuard();
const grandTotalGuard = createLatestRequestGuard();

export const actions = {
    [actionTypes.getData]({ commit }, { period, aabSiteIds, timezone, noCommit, slots, source }) {
        !noCommit && commit(mutationTypes.yieldStart);
        // noCommit callers (CSV export) don't write to the store, so they neither
        // cancel nor get cancelled by the dashboard fetches.
        const guard = noCommit ? null : yieldDataGuard.start();

        let data = prepareBodyForInterval(period, aabSiteIds, timezone, source);

        if (typeof data.period != "string") {
            let dateFrom = new Date(data.period.dateFrom);
            let dateFromYYYYMMDD = dateFrom.toISOString().split("T")[0];

            let dateTo = new Date(data.period.dateTo);
            let dateToYYYYMMDD = dateTo.toISOString().split("T")[0];

            data["period"] = "custom_range";
            data["date_from"] = dateFromYYYYMMDD;
            data["date_to"] = dateToYYYYMMDD;
        }

        data["slots"] = slots;

        return axios({
            method: "post",
            url: "sql/yield",
            data: data,
            cancelToken: guard?.cancelToken,
        }).then(
            ({ data }) => {
                if (guard && !guard.isLatest()) {
                    // Superseded: resolve with nothing so the caller doesn't
                    // apply stale totals either.
                    return undefined;
                }
                guard && commit(mutationTypes.yieldSuccess, data);
                return data;
            },
            () => {
                // A cancelled request is never the latest one, so its failure
                // must not flip isLoading while the newer request is in flight.
                if (guard && guard.isLatest()) {
                    commit(mutationTypes.yieldFailure);
                }
            }
        );
    },
    // TODO: not ready yet!
    [actionTypes.getGrandTotal]({ commit }, { aabSiteIds, timezone }) {
        commit(mutationTypes.grandTotalStart);
        const guard = grandTotalGuard.start();
        const jan2020 = new Date(2020, 1);
        const today = new Date();

        return axios({
            method: "post",
            url: "sql/yield-total",
            data: { date_from: jan2020, date_to: today, aab_sites_ids: aabSiteIds, timezone: timezone },
            cancelToken: guard.cancelToken,
        }).then(
            ({ data }) => {
                if (!guard.isLatest()) {
                    return;
                }
                if (data?.metadata?.["total_revenue"]) {
                    commit(mutationTypes.grandTotalSuccess, data.metadata);
                } else {
                    commit(mutationTypes.grandTotalFailure);
                }
            },
            () => {
                if (guard.isLatest()) {
                    commit(mutationTypes.grandTotalFailure);
                }
            }
        );
    },

    [actionTypes.editRecord]({ commit }, payload) {
        commit(mutationTypes.recordEditDataStart);

        return axios({
            method: "put",
            url: "sql/daily-data/" + payload.id,
            data: {
                publisher_revenue_custom: payload.publisher_revenue_custom,
                is_approved: payload.is_approved,
            },
        }).then(
            (data) => {
                commit(mutationTypes.recordEditDataSuccess, data.data);
                // showMessage(data.data, "edit", "daily-data");
                window.location.reload();
            },
            () => {
                commit(mutationTypes.recordEditDataFailure);
            }
        );
    },
};
